/**
 * Time utility functions for Mountain Standard Time (Denver)
 * All timestamps should be displayed in MST with 12-hour AM/PM format
 */

// Mountain Time timezone
export const TIMEZONE = 'America/Denver';

/**
 * Format a time string (HH:MM) to 12-hour AM/PM format
 * @param {string} time24 - Time in 24-hour format (e.g., "14:00")
 * @returns {string} Time in 12-hour format (e.g., "2:00 PM")
 */
export const formatTime12Hour = (time24) => {
  if (!time24) return '';
  
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

/**
 * Format a date string to Mountain Time with readable format
 * @param {string} dateStr - ISO date string or YYYY-MM-DD
 * @returns {string} Formatted date string
 */
export const formatDateMST = (dateStr) => {
  if (!dateStr) return '';
  
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    timeZone: TIMEZONE,
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Format a datetime string to Mountain Time with full date and time
 * @param {string} dateTimeStr - ISO datetime string
 * @returns {string} Formatted datetime string
 */
export const formatDateTimeMST = (dateTimeStr) => {
  if (!dateTimeStr) return '';
  
  const date = new Date(dateTimeStr);
  return date.toLocaleString('en-US', {
    timeZone: TIMEZONE,
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Format just the time portion of a datetime to Mountain Time
 * @param {string} dateTimeStr - ISO datetime string
 * @returns {string} Formatted time string (e.g., "2:30 PM")
 */
export const formatTimeMST = (dateTimeStr) => {
  if (!dateTimeStr) return '';
  
  const date = new Date(dateTimeStr);
  return date.toLocaleTimeString('en-US', {
    timeZone: TIMEZONE,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Format a short date for display (e.g., "Jan 23, 2026")
 * @param {string} dateStr - ISO date string or YYYY-MM-DD
 * @returns {string} Short formatted date
 */
export const formatShortDateMST = (dateStr) => {
  if (!dateStr) return '';
  
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    timeZone: TIMEZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Get current date in Mountain Time as YYYY-MM-DD
 * @returns {string} Current date in YYYY-MM-DD format
 */
export const getCurrentDateMST = () => {
  const now = new Date();
  return now.toLocaleDateString('en-CA', { timeZone: TIMEZONE }); // en-CA gives YYYY-MM-DD format
};

/**
 * Check if a date is in the past (Mountain Time)
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {boolean} True if date is in the past
 */
export const isDatePastMST = (dateStr) => {
  const today = getCurrentDateMST();
  return dateStr < today;
};
